/**
 * Keşif SSE wire codec — zod discriminated union for every `CrawlEvent`.
 *
 * Mirrors `crawler/events.py`. Kept separate from `eventCodec.ts` because the
 * two contracts version independently; sharing one union would mean a debate
 * event change could reject crawl frames and vice versa.
 *
 * Two deliberate leniencies, both learned from the `persona.hydrate` incident
 * documented in `eventCodec.ts` (a backend enum grew, the FE union did not,
 * and `z.discriminatedUnion` silently dropped EVERY frame of that type):
 *
 *   1. Every closed enum uses `.catch(...)` with a safe fallback, so a new
 *      backend phase/tier/kind degrades one FIELD instead of killing the
 *      whole frame.
 *   2. Numeric and string fields that only feed display carry defaults, so a
 *      field the backend adds later cannot invalidate existing events.
 *
 * What is NOT lenient: `type` and `jobId`. A frame without a routable
 * discriminator or an owner is genuinely unusable and must be dropped.
 */
import { z } from "zod";
import type { CrawlEvent } from "../domain/kesif";

/* ---------- atoms ---------- */

const ScopeModeSchema = z
  .enum([
    "exact-host",
    "subdomains",
    "registrable-domain",
    "allowlist",
    "unrestricted",
  ])
  .catch("subdomains");

const RenderModeSchema = z.enum(["auto", "http-only", "browser-always"]).catch("auto");

const FetchTierSchema = z
  .enum(["cache", "http", "browser", "screenshot"])
  .catch("http");

const AssetKindSchema = z
  .enum(["image", "pdf", "document", "video", "audio", "archive", "other"])
  .catch("other");

const JobStatusSchema = z.enum([
  "queued",
  "running",
  "paused",
  "completed",
  "cancelled",
  "failed",
]);

const JobPhaseSchema = z
  .enum([
    "bootstrap",
    "planning",
    "crawling",
    "saturating",
    "indexing",
    "finalizing",
    "done",
  ])
  .catch("crawling");

const PageTypeSchema = z
  .enum([
    "home",
    "article",
    "listing",
    "product",
    "docs",
    "contact",
    "legal",
    "media",
    "search",
    "other",
  ])
  .catch("other");

const StalenessSchema = z
  .enum(["news", "social", "market", "reference", "academic", "regulation"])
  .catch("reference");

const nonNegative = z.number().finite().min(0).catch(0);
const ratio = z.number().finite().min(0).max(1).catch(0);
const text = (fallback = "") => z.string().catch(fallback);

/* ---------- composites ---------- */

export const CrawlPolicySchema = z.object({
  scopeMode: ScopeModeSchema,
  allowedDomains: z.array(z.string()).catch([]),
  blockedDomains: z.array(z.string()).catch([]),
  includePatterns: z.array(z.string()).catch([]),
  excludePatterns: z.array(z.string()).catch([]),
  maxDepth: nonNegative,
  maxPages: nonNegative,
  maxPagesPerHost: nonNegative,
  maxExternalHops: nonNegative,
  followSitemaps: z.boolean().catch(true),

  requestsPerSecond: z.number().finite().positive().catch(1),
  respectRobots: z.boolean().catch(true),
  ownerDeclared: z.boolean().catch(false),
  maxDurationMinutes: nonNegative,

  renderMode: RenderModeSchema,
  captureScreenshots: z.boolean().catch(false),
  recrawlTtlHours: z.number().int().min(0).nullable().catch(null),

  extractAssets: z.boolean().catch(true),
  assetKinds: z.array(AssetKindSchema).catch([]),
  extractTables: z.boolean().catch(true),
  extractStructuredData: z.boolean().catch(true),
  minTextChars: nonNegative,

  intent: text(),
  llmPlan: z.boolean().catch(true),
  llmPrioritise: z.boolean().catch(true),
  llmExtract: z.boolean().catch(false),
  extractSchema: z.record(z.unknown()).nullable().catch(null),

  pushToCorpus: z.boolean().catch(true),
  corpusStalenessClass: StalenessSchema,

  adaptiveStop: z.boolean().catch(true),
});

export const CountersSchema = z.object({
  discovered: nonNegative,
  fetched: nonNegative,
  failed: nonNegative,
  skipped: nonNegative,
  duplicate: nonNegative,
  pending: nonNegative,
  assetsSaved: nonNegative,
  assetsSkipped: nonNegative,
  bytesFetched: nonNegative,
  assetBytes: nonNegative,
  llmCalls: nonNegative,
  corpusRows: nonNegative,
  hostsSeen: nonNegative,
});

export const HostSchema = z.object({
  host: z.string(),
  allowed: z.boolean().catch(true),
  disallowReason: text(),
  robotsStatus: z.number().int().nullable().catch(null),
  robotsBypass: z.boolean().catch(false),
  crawlDelayS: z.number().finite().nullable().catch(null),
  effectiveDelayS: z.number().finite().min(0).catch(1),
  sitemapCount: nonNegative,
  pagesFetched: nonNegative,
  bytesFetched: nonNegative,
  consecutiveErrors: nonNegative,
  circuitOpen: z.boolean().catch(false),
});

export const PageSchema = z.object({
  id: z.string(),
  url: z.string(),
  finalUrl: text(),
  host: text(),
  depth: nonNegative,
  httpStatus: z.number().int().nullable().catch(null),
  contentType: text(),
  title: text(),
  description: text(),
  lang: text(),
  author: text(),
  publishedAt: z.string().nullable().catch(null),
  charCount: nonNegative,
  wordCount: nonNegative,
  pageType: PageTypeSchema,
  fetchTier: FetchTierSchema,
  fetchMs: nonNegative,
  qualityScore: ratio,
  outlinkCount: nonNegative,
  assetCount: nonNegative,
  duplicateOf: z.string().nullable().catch(null),
  llmSummary: text(),
  llmTopics: z.array(z.string()).catch([]),
  structured: z.record(z.unknown()).catch({}),
  error: text(),
  fetchedAt: text(),
  extractedText: z.string().nullable().optional(),
  markdown: z.string().nullable().optional(),
});

export const AssetSchema = z.object({
  id: z.string(),
  pageId: z.string().nullable().catch(null),
  url: z.string(),
  kind: AssetKindSchema,
  role: text("inline"),
  contentType: text(),
  bytes: nonNegative,
  width: z.number().int().nullable().catch(null),
  height: z.number().int().nullable().catch(null),
  altText: text(),
  caption: text(),
  visionSummary: text(),
  status: text("saved"),
  skipReason: text(),
  downloadUrl: text(),
  createdAt: text(),
});

export const PlanSuggestionSchema = z.object({
  summary: text(),
  suggestedMaxDepth: z.number().int().nullable().catch(null),
  suggestedMaxPages: z.number().int().nullable().catch(null),
  includePatterns: z.array(z.string()).catch([]),
  excludePatterns: z.array(z.string()).catch([]),
  priorityKeywords: z.array(z.string()).catch([]),
  sitemapUrls: z.array(z.string()).catch([]),
  detectedSiteType: text(),
  warnings: z.array(z.string()).catch([]),
});

export const JobSchema = z.object({
  id: z.string(),
  name: text(),
  seedUrls: z.array(z.string()).catch([]),
  policy: CrawlPolicySchema,
  status: JobStatusSchema,
  phase: JobPhaseSchema,
  counters: CountersSchema,
  plan: z.record(z.unknown()).catch({}),
  error: text(),
  degradedReason: text(),
  createdAt: text(),
  startedAt: z.string().nullable().catch(null),
  finishedAt: z.string().nullable().catch(null),
  updatedAt: z.string().nullable().catch(null),
  live: z.boolean().optional(),
});

export const FrontierStatsSchema = z.object({
  pending: nonNegative,
  leased: nonNegative,
  done: nonNegative,
  failed: nonNegative,
  skipped: nonNegative,
});

/* ---------- event union ---------- */

export const CrawlEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("crawl.job"),
    jobId: z.string(),
    status: JobStatusSchema,
    phase: JobPhaseSchema,
    counters: CountersSchema,
    name: text(),
    error: text(),
    degradedReason: text(),
  }),
  z.object({
    type: z.literal("crawl.progress"),
    jobId: z.string(),
    counters: CountersSchema,
    phase: JobPhaseSchema,
    progress: ratio,
    activeHosts: nonNegative,
    inFlight: nonNegative,
    pagesPerMinute: nonNegative,
    etaSeconds: z.number().int().min(0).nullable().catch(null),
  }),
  z.object({
    type: z.literal("crawl.page"),
    jobId: z.string(),
    page: PageSchema,
  }),
  z.object({
    type: z.literal("crawl.asset"),
    jobId: z.string(),
    pageId: z.string().nullable().catch(null),
    assets: z.array(AssetSchema).catch([]),
  }),
  z.object({
    type: z.literal("crawl.host"),
    jobId: z.string(),
    host: HostSchema,
  }),
  z.object({
    type: z.literal("crawl.plan"),
    jobId: z.string(),
    plan: PlanSuggestionSchema,
  }),
  z.object({
    type: z.literal("crawl.log"),
    jobId: z.string(),
    level: z.enum(["info", "warn", "error"]).catch("info"),
    message: text(),
    url: text(),
    at: nonNegative,
  }),
  z.object({
    type: z.literal("crawl.saturated"),
    jobId: z.string(),
    novelRatio: ratio,
    windowSize: nonNegative,
    reason: text(),
  }),
  z.object({
    type: z.literal("crawl.done"),
    jobId: z.string(),
    status: JobStatusSchema,
    counters: CountersSchema,
    stopReason: text(),
    elapsedMs: nonNegative,
    summary: z.record(z.unknown()).catch({}),
  }),
  z.object({
    type: z.literal("crawl.error"),
    jobId: text(),
    code: z.string(),
    message: text(),
  }),
]);

export interface CrawlCodecLogger {
  warn(msg: string, meta?: unknown): void;
}

const consoleLogger: CrawlCodecLogger = {
  warn: (msg, meta) => {
    if (typeof console !== "undefined") {
      console.warn(`[crawlEventCodec] ${msg}`, meta ?? "");
    }
  },
};

/**
 * Validate one frame. Returns `null` for anything unusable — an unknown
 * `type` from a newer backend is dropped rather than thrown, which is what
 * makes adding an event backwards compatible with deployed bundles.
 */
export function decodeCrawlEvent(
  raw: unknown,
  logger: CrawlCodecLogger = consoleLogger,
): CrawlEvent | null {
  const parsed = CrawlEventSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn("rejected crawl SSE frame", {
      type: (raw as { type?: unknown } | null)?.type,
      issues: parsed.error.issues.slice(0, 3),
    });
    return null;
  }
  return parsed.data as CrawlEvent;
}
